import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';
import { Loader2, CreditCard, FileText, LogOut, Mic, X, Eye, ChevronDown, ClipboardCheck } from 'lucide-react';
import { SessionDetailsView } from '../components/SessionDetailsView';
import type { TabType } from '../components/SessionDetailsView';
import type { TranscriptEntry } from '../components/GeminiLiveWidget';

interface SessionDetail {
  id: string;
  started_at?: string;
  ended_at?: string;
  duration_seconds?: number;
  scenario_id?: number;
  transcript?: TranscriptEntry[];
  analysis?: any;
  scientific_scenario?: string;
  executive_scenario?: string;
  scenario_text?: string;
}

export function Dashboard() {
  const { user, loading: authLoading, getToken, signOut, signInWithGoogle } = useAuth();
  const [balance, setBalance] = useState<{ credits: number; equivalent_minutes: number; currency: string } | null>(null);
  const [sessions, setSessions] = useState<Array<{ id: string; started_at?: string; ended_at?: string; duration_seconds?: number; scenario_id?: number; transcript?: TranscriptEntry[]; analysis?: any }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<SessionDetail | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('results');
  const [loadingSession, setLoadingSession] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadDashboardData = async () => {
    const token = await getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const [balanceRes, sessionsRes] = await Promise.all([
        apiFetch('/api/balance', { token }),
        apiFetch('/api/sessions', { token }),
      ]);
      if (balanceRes.ok) {
        const data = await balanceRes.json();
        setBalance(data);
      }
      if (sessionsRes.ok) {
        const data = await sessionsRes.json();
        setSessions(data.sessions ?? []);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  // Poll for session updates if any session is being analyzed
  useEffect(() => {
    if (!user || loading) return;
    
    // Only poll if there's a session that ended very recently (last 5 minutes) and has no analysis
    const now = new Date();
    const hasAnalyzingSession = sessions.some(s => {
      if (!s.ended_at || s.analysis) return false;
      const endedAt = new Date(s.ended_at);
      const diffMinutes = (now.getTime() - endedAt.getTime()) / (1000 * 60);
      return diffMinutes < 5; // Only poll for sessions ended in the last 5 minutes
    });

    if (!hasAnalyzingSession) return;

    const interval = setInterval(async () => {
      const token = await getToken();
      if (!token) return;
      
      try {
        const res = await apiFetch('/api/sessions', { token });
        if (res.ok) {
          const data = await res.json();
          const newSessions = data.sessions ?? [];
          
          // Only update if something changed (e.g., a session got an analysis)
          const hasChanges =
            JSON.stringify(newSessions.map((s: { analysis?: any }) => !!s.analysis)) !==
            JSON.stringify(sessions.map((s: { analysis?: any }) => !!s.analysis));
          
          if (hasChanges) {
            setSessions(newSessions);
          }
        }
      } catch (e) {
        console.error('Failed to poll sessions:', e);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(interval);
  }, [user, sessions, loading, getToken]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadDashboardData();
    })();
    return () => { cancelled = true; };
  }, [user, getToken]);

  // Check for successful checkout return and refresh balance
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      // User returned from successful Stripe checkout, refresh balance
      setTimeout(() => {
        loadDashboardData();
      }, 1000); // Small delay to allow webhook processing
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [user]);

  const handleBuyCredits = async () => {
    const token = await getToken();
    if (!token) return;
    try {
      const res = await apiFetch('/api/create-checkout-session', {
        method: 'POST',
        token,
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data?.url) window.location.href = data.url;
      else setError(data?.detail || 'Failed to create checkout');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  };

  const formatTime = (sec?: number) => {
    if (sec == null) return '–';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '–';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const viewSessionDetails = async (sessionId: string) => {
    setLoadingSession(true);
    try {
      const token = await getToken();
      if (!token) return;
      
      const url = `/api/sessions/${sessionId}`;
      const res = await apiFetch(url, { token });
      if (res.ok) {
        const data = await res.json();
        setSelectedSession(data);
      } else {
        const errText = await res.text().catch(() => 'No body');
        try {
          const errJson = JSON.parse(errText);
          setError(errJson.detail || errJson.message || 'Failed to load session details');
        } catch {
          setError(errText || 'Failed to load session details');
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoadingSession(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ color: 'var(--text)' }}>
        <Loader2 className="w-10 h-10 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen px-4 py-8">
        <div className="container mx-auto max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between mb-8"
          >
            <h1 className="text-2xl font-semibold" style={{ color: 'var(--text)' }}>
              Dashboard
            </h1>
            <Link
              to="/"
              className="btn-gradient flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold"
            >
              <Mic className="w-4 h-4" />
              <span className="hidden sm:inline">Back to voice call</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center justify-center"
            style={{ minHeight: '60vh' }}
          >
            <div className="glass-card p-12 text-center max-w-md w-full">
              <h2 className="text-2xl font-semibold mb-2" style={{ color: 'var(--text)' }}>
                Sign in required
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>
                Please sign in to view your dashboard
              </p>
              <button
                onClick={() => signInWithGoogle()}
                className="btn-gradient w-full py-3 rounded-xl font-semibold"
              >
                Sign in with Google
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const userInitial = user.displayName?.[0] || user.email?.[0] || '?';

  return (
    <div className="min-h-screen px-4 py-8">
      <div className="container mx-auto max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8"
        >
          <div className="flex items-center justify-between w-full sm:w-auto">
            <h1 className="text-2xl font-semibold" style={{ color: 'var(--text)' }}>
              Dashboard
            </h1>
            <div className="flex items-center gap-3 sm:hidden">
              <Link
                to="/"
                className="btn-gradient flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold"
              >
                <Mic className="w-4 h-4" />
                <span>Back</span>
              </Link>
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all hover:ring-2 hover:ring-white/20"
                  style={{
                    background: 'linear-gradient(135deg, #6ee7ff 0%, #b46eff 100%)',
                    color: '#000',
                  }}
                >
                  {userInitial.toUpperCase()}
                </button>
                <AnimatePresence>
                  {showUserMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 mt-2 w-48 glass-card p-2 z-50 shadow-xl border border-white/10"
                    >
                      <div className="px-3 py-2 border-b border-white/5 mb-1">
                        <p className="text-xs font-medium truncate" style={{ color: 'var(--muted)' }}>
                          {user.email}
                        </p>
                      </div>
                      <button
                        onClick={() => signOut()}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium hover:bg-white/10 transition-colors"
                        style={{ color: '#ff4b4b' }}
                      >
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-4">
            <Link
              to="/"
              className="btn-gradient flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold"
            >
              <Mic className="w-4 h-4" />
              Back to voice call
            </Link>
            
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 p-1 pr-3 rounded-full transition-all hover:bg-white/5"
                style={{ border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #6ee7ff 0%, #b46eff 100%)',
                    color: '#000',
                  }}
                >
                  {userInitial.toUpperCase()}
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} style={{ color: 'var(--muted)' }} />
              </button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 mt-2 w-56 glass-card p-2 z-50 shadow-xl border border-white/10"
                  >
                    <div className="px-3 py-2 border-b border-white/5 mb-1">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>
                        {user.displayName || 'User'}
                      </p>
                      <p className="text-xs truncate" style={{ color: 'var(--muted)' }}>
                        {user.email}
                      </p>
                    </div>
                    <button
                      onClick={() => signOut()}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium hover:bg-white/10 transition-colors"
                      style={{ color: '#ff4b4b' }}
                    >
                      <LogOut className="w-4 h-4" />
                      Sign out
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        {error && (
          <div
            className="mb-6 p-4 rounded-xl"
            style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#ef4444',
            }}
          >
            {error}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="glass-card p-6 mb-8"
        >
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text)' }}>
            Credit balance
          </h2>
          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--accent)' }} />
          ) : balance != null ? (
            <div>
              <p className="text-3xl font-bold mb-1" style={{ color: 'var(--accent)' }}>
                {balance.credits} Credits
              </p>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                ≈ {Math.floor(balance.equivalent_minutes)} minutes talk time
              </p>
            </div>
          ) : (
            <p style={{ color: 'var(--muted)' }}>Unable to load balance.</p>
          )}
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleBuyCredits}
              className="btn-gradient flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm"
            >
              <CreditCard className="w-4 h-4" />
              Buy credits
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="glass-card p-6"
        >
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text)' }}>
            Conversation history
          </h2>
          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--accent)' }} />
          ) : sessions.length === 0 ? (
            <p style={{ color: 'var(--muted)' }}>No sessions yet.</p>
          ) : (
            <div className="space-y-2">
              {sessions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => viewSessionDetails(s.id)}
                  className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-white/8 transition-all group"
                  style={{ background: 'rgba(255,255,255,0.04)' }}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4" style={{ color: 'var(--muted)' }} />
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                          Scenario {s.scenario_id ?? '–'}
                        </span>
                        {(() => {
                          const now = new Date();
                          const endedAt = s.ended_at ? new Date(s.ended_at) : null;
                          const isRecent = endedAt && (now.getTime() - endedAt.getTime()) / (1000 * 60) < 5;
                          
                          if (s.ended_at && !s.analysis && isRecent) {
                            return (
                              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-white/5 border border-white/10">
                                <Loader2 className="w-3 h-3 animate-spin" style={{ color: 'var(--accent)' }} />
                                <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                                  Analyzing
                                </span>
                              </div>
                            );
                          }
                          
                          if (s.ended_at && !s.analysis && !isRecent) {
                            return (
                              <span className="text-xs px-2 py-0.5 rounded" style={{
                                background: 'rgba(239, 68, 68, 0.1)',
                                color: '#ef4444'
                              }}>
                                Failed
                              </span>
                            );
                          }

                          if (s.analysis) {
                            return (
                              <span className="text-xs px-2 py-0.5 rounded" style={{
                                background: 'rgba(16, 185, 129, 0.2)',
                                color: '#10b981'
                              }}>
                                Analyzed
                              </span>
                            );
                          }
                          
                          return null;
                        })()}
                      </div>
                      <span className="text-xs" style={{ color: 'var(--muted)' }}>
                        {formatTime(s.duration_seconds)} • {formatDate(s.started_at)}
                      </span>
                    </div>
                  </div>
                  <Eye className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#6ee7ff' }} />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Session Detail Modal */}
        <AnimatePresence>
          {selectedSession && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedSession(null)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="glass-card max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Fixed Header: Title, Close Button, and Tabs */}
                <div className="flex flex-col backdrop-blur-xl bg-[#0b0d12]/80 shrink-0 z-30">
                  <div className="flex items-center justify-between px-6 pt-4 pb-2">
                    <h2 className="text-xl font-semibold text-white font-sans">
                      Session Details
                    </h2>
                    <button
                      onClick={() => setSelectedSession(null)}
                      className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                      style={{ color: 'var(--muted)' }}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Tabs moved into the fixed header for perfect alignment */}
                  {selectedSession.analysis && selectedSession.transcript && (
                    <div className="px-6 pb-4 border-b border-white/5">
                      <div className="flex p-1 bg-white/5 rounded-xl border border-white/10">
                        <button
                          onClick={() => setActiveTab('details')}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                            activeTab === 'details' 
                              ? 'bg-white/15 text-white shadow-sm' 
                              : 'text-muted hover:text-white'
                          }`}
                        >
                          <FileText className="w-4 h-4 shrink-0" />
                          <span className="truncate">Case Details</span>
                        </button>
                        <button
                          onClick={() => setActiveTab('results')}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-medium transition-all text-sm relative ${
                            activeTab === 'results' 
                              ? 'bg-white/15 text-white shadow-sm' 
                              : 'text-muted hover:text-white'
                          }`}
                        >
                          <ClipboardCheck className="w-4 h-4 shrink-0" />
                          <span className="truncate">Results</span>
                          
                          {selectedSession.analysis && (
                            <div className="absolute -top-1 -right-0.5 px-1 py-0.5 rounded-[4px] text-[7px] font-black tracking-tighter uppercase shadow-sm border border-[#10b981]/20" style={{
                              background: '#10b981',
                              color: '#fff',
                              lineHeight: '1',
                            }}>
                              Analyzed
                            </div>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Scrollable Content Area */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                  <div className="p-6">
                    {loadingSession ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#6ee7ff' }} />
                      </div>
                    ) : selectedSession.analysis && selectedSession.transcript ? (
                      <SessionDetailsView
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        analysis={selectedSession.analysis}
                        transcript={selectedSession.transcript}
                        scenario={{
                          id: selectedSession.scenario_id ?? 0,
                          text: selectedSession.scenario_text || selectedSession.analysis?.scenario_text || 'Medical Case',
                          scientificScenario: selectedSession.scientific_scenario || selectedSession.analysis?.scientific_scenario
                        }}
                        duration={selectedSession.duration_seconds}
                        hideTabs={true}
                      />
                    ) : (
                      <div className="text-center py-12 px-6">
                        <p style={{ color: 'var(--muted)' }}>
                          {selectedSession.transcript 
                            ? 'No analysis available for this session.'
                            : 'No transcript available for this session.'}
                        </p>
                        <p className="text-sm mt-2" style={{ color: 'var(--muted)' }}>
                          This session was completed before the analysis feature was added.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
